import { ProtectedRoute } from "@/core/auth/guards/ProtectedRoute";
import { AppointmentAddPage } from "@/features/appointments/pages/AppointmentAddPage";
import { CalendarEventEditPage } from "@/features/calendar-events/pages/CalendarEventEditPage";
import { CalendarEventDetailsPage } from "@/features/calendar-events/pages/CalendarEventDetailsPage";
import { AppointmentTypePage } from "@/features/appointments/pages/AppointmentTypePage";
import { AppointmentDetailsPage } from "@/features/appointments/pages/AppointmentDetailsPage";
import { AppointmentEditPage } from "@/features/appointments/pages/AppointmentEditPage";
import { AppointmentsListPage } from "@/features/appointments/pages/AppointmentsListPage";
import { AppsPage } from "@/features/apps/pages/AppsListPage";
import { FormsListPage } from "@/features/forms/pages/FormsListPage";
import { FormDetailsPage } from "@/features/forms/pages/FormDetailsPage";
import { FormPreviewOverlay } from "@/features/forms/components/FormPreviewOverlay";
import { AboutPage } from "@/features/apps/pages/AboutPage";
import { AvailabilitiesPage } from "@/features/availability/pages/AvailabilitiesPage";
import { BusinessProvider } from "@/features/business/contexts/BusinessProvider";
import { DefaultBusinessGuard } from "@/features/business/guard/DefaultBusinessGuard";
import { ClientAddPage } from "@/features/clients/pages/ClientAddPage";
import { ClientDetailsPage } from "@/features/clients/pages/ClientDetailsPage";
import { ClientEditPage } from "@/features/clients/pages/ClientEditPage";
import { ClientsListPage } from "@/features/clients/pages/ClientsListPage";
import { DashboardPage } from "@/features/dashboard/pages/DashboardPage";
import { AssistantPage } from "@/features/dashboard/pages/AssistantPage";
import { ConversationsListPage } from "@/features/messages/pages/ConversationsListPage";
import { MessagesListPage } from "@/features/messages/pages/MessagesListPage";
import { NotificationsPage } from "@/features/notifications/pages/NotificationsPage";
import { OnboardingGuard } from "@/features/onboarding/guard/OnboardingGuard";
import { WelcomePage } from "@/features/onboarding/pages/WelcomePage";
import { ProfilePage } from "@/features/profile/pages/ProfilePage";
import { CustomerEmailsPage } from "@/features/customer-emails/pages/CustomerEmailsPage";
import { AppointmentRulesPage } from "@/features/business/pages/AppointmentRulesPage";
import { BusinessWebsitePage } from "@/features/business-website/pages/BusinessWebsitePage";
import { StripePage } from "@/features/stripe/pages/StripePage";
import { StripeFinancesPage } from "@/features/stripe/pages/StripeFinancesPage";
import { StripeReturnPage } from "@/features/stripe/pages/StripeReturnPage";
import { PaymentMethodAddPage } from "@/features/payment-methods/pages/PaymentMethodAddPage";
import { PaymentMethodSetupPage } from "@/features/payment-methods/pages/PaymentMethodSetupPage";
import { AppointmentChargePage } from "@/features/payments/pages/AppointmentChargePage";
import { AppointmentPaymentPage } from "@/features/payments/pages/AppointmentPaymentPage";
import { CustomerPaymentPage } from "@/features/payments/pages/CustomerPaymentPage";
import { StripeHostBridge } from "@/features/stripe/components/StripeHostBridge";
import { SearchPage } from "@/features/search/pages/SearchPage";
import { ServiceAddPage } from "@/features/services/pages/ServiceAddPage";
import { ServiceDetailsPage } from "@/features/services/pages/ServiceDetailsPage";
import { ServiceEditPage } from "@/features/services/pages/ServiceEditPage";
import { ServicesListPage } from "@/features/services/pages/ServicesListPage";
import { LoginPage } from "@/features/user/pages/LoginPage";
import { PasswordRecoveryPage } from "@/features/user/pages/PasswordRecoveryPage";
import { SignupPage } from "@/features/user/pages/SignupPage";
import { SignupConfirmationPage } from "@/features/user/pages/SignupConfirmationPage";
import { OAuthConsentPage } from "@/features/oauth/pages/OAuthConsentPage";
import { ConnectedAppsPage } from "@/features/oauth/pages/ConnectedAppsPage";
import { authService, signupConfirmationPath } from "@/core/auth/services/authService";
import { AppLayout } from "@/shared/layouts/AppLayout";
import { AuthLayout } from "@/shared/layouts/AuthLayout";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { useRouteOverlay } from "@/shared/hooks/useRouteOverlay";
import { matchPath, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

export function AppRoutes() {
  const location = useLocation();
  const searchRoute = matchPath("/b/:businessId/l/:locationId/search", location.pathname);
  const dashboardPath = searchRoute
    ? `/b/${searchRoute.params.businessId}/l/${searchRoute.params.locationId}`
    : "/";
  const { backgroundLocation } = useRouteOverlay(dashboardPath);
  const overlay = searchRoute ? (
    <Routes location={location}>
      <Route path="/b/:businessId/l/:locationId/search" element={<SearchPage />} />
    </Routes>
  ) : undefined;

  return (
    <Routes location={searchRoute ? backgroundLocation ?? dashboardPath : location}>
      {/* Auth routes */}
      <Route path="/stripe/return" element={<StripeReturnPage />} />
      <Route path="/payment-methods/setup" element={<PaymentMethodSetupPage />} />
      <Route path="/payments/pay" element={<CustomerPaymentPage />} />
      <Route element={<AuthLayout />}>
        <Route path="/oauth/consent" element={<OAuthConsentPage />} />
        <Route element={authService.kind === "host" ? <Navigate to="/" replace /> : <Outlet />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path={signupConfirmationPath} element={<SignupConfirmationPage />} />
          <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
        </Route>
        <Route path="/welcome" element={<WelcomePage />} />
      </Route>

      {/* Protected app routes */}
      <Route
        element={
          <ProtectedRoute returnTo={`${location.pathname}${location.search}${location.hash}`}>
            <BusinessProvider>
              <OnboardingGuard>
                <DefaultBusinessGuard>
                  <StripeHostBridge />
                  <AppLayout overlay={overlay} />
                  <FormPreviewOverlay />
                </DefaultBusinessGuard>
              </OnboardingGuard>
            </BusinessProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/b/:businessId/l/:locationId">
          <Route index element={<DashboardPage />} />
          <Route path="assistant" element={<AssistantPage />} />
          <Route path="apps" element={<AppsPage />} />
          <Route path="forms" element={<FormsListPage />} />
          <Route path="forms/:formId" element={<FormDetailsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="availabilities" element={<AvailabilitiesPage />} />
          <Route path="clients" element={<ClientsListPage />} />
          <Route path="clients/new" element={<ClientAddPage />} />
          <Route path="clients/:clientId" element={<ClientDetailsPage />} />
          <Route path="clients/:clientId/edit" element={<ClientEditPage />} />
          <Route path="clients/:clientId/payment-methods/new" element={<PaymentMethodAddPage />} />
          <Route path="services" element={<ServicesListPage />} />
          <Route path="services/new" element={<ServiceAddPage />} />
          <Route path="services/:serviceId" element={<ServiceDetailsPage />} />
          <Route
            path="services/:serviceId/edit"
            element={<ServiceEditPage />}
          />
          <Route path="appointments" element={<AppointmentsListPage />} />
          <Route path="appointments/new" element={<AppointmentTypePage />} />
          <Route path="appointments/new/client" element={<AppointmentAddPage />} />
          <Route path="appointments/:appointmentId/payments/new" element={<AppointmentChargePage />} />
          <Route path="appointments/:appointmentId/payments/:paymentId" element={<AppointmentPaymentPage />} />
          <Route path="calendar-events/new/personal" element={<CalendarEventEditPage kind="personal" />} />
          <Route path="calendar-events/new/blocked" element={<CalendarEventEditPage kind="blocked" />} />
          <Route path="calendar-events/:eventId" element={<CalendarEventDetailsPage />} />
          <Route path="calendar-events/:eventId/edit" element={<CalendarEventEditPage />} />
          <Route
            path="appointments/:appointmentId"
            element={<AppointmentDetailsPage />}
          />
          <Route
            path="appointments/:appointmentId/edit"
            element={<AppointmentEditPage />}
          />
          <Route
            path="conversations/:conversationId/messages"
            element={<MessagesListPage />}
          />
          <Route path="conversations" element={<ConversationsListPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="customer-emails" element={<CustomerEmailsPage />} />
          <Route path="appointment-rules" element={<AppointmentRulesPage />} />
          <Route path="website" element={<BusinessWebsitePage />} />
          <Route path="stripe" element={<StripePage />} />
          <Route path="finances" element={<StripeFinancesPage />} />
          <Route path="connected-apps" element={<ConnectedAppsPage />} />
        </Route>
        <Route path="/b/:businessId" element={
          <Page>
            <PageHeader title="Locations" />
            <PageContent><LoadingSpinner message="Loading locations..." /></PageContent>
          </Page>
        } />
        <Route path="/" element={
          <Page>
            <PageHeader title="Businesses" />
            <PageContent><LoadingSpinner message="Loading businesses..." /></PageContent>
          </Page>
        } />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
